import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import Avatar from "@/components/Avatar";
import { TIERS } from "@/lib/tiers";
import { X, Search } from "lucide-react";

/**
 * User picker for the "Requested By" supporter credit.
 *
 *   props:
 *     valueId         — currently-selected user id ("" if none)
 *     valueDisplay    — currently-selected user object {id, name, avatar_url, role}
 *                       or null (used to render the pill without re-fetching)
 *     onChange(user|null)  — called with the picked user object, or null to clear
 *
 * Search is debounced and hits the existing `/api/admin/users?q=` endpoint
 * (admin-only). The dropdown lists name + email + role badge so admins can
 * disambiguate similarly-named users.
 */
export default function RequestedByPicker({ valueId, valueDisplay, onChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (!q.trim()) { setResults([]); return; }
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/users", { params: { q: q.trim(), limit: 10 } });
        setResults(data.users || []);
      } catch (e) {
        console.error("user search failed", e);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [q, open]);

  // Click-outside to close
  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const selected = valueDisplay && (valueDisplay.id === valueId || valueDisplay.id == null)
    ? valueDisplay
    : null;

  const pickUser = (u) => {
    onChange({
      id: u.id,
      name: u.name || (u.email ? u.email.split("@")[0] : "Cultist"),
      avatar_url: u.avatar_url || "",
      role: u.role || "member",
      badges: u.badges || [],
    });
    setOpen(false);
    setQ("");
    setResults([]);
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid="post-requested-by-picker">
      <Label className="text-xs text-zinc-400">
        Requested By <span className="text-zinc-600">(optional — pick a supporter)</span>
      </Label>

      {selected ? (
        // Selected state — show pill with the chosen user + clear button
        <div className="mt-1 flex items-center gap-2 p-2 rounded-md border border-rose-500/40 bg-rose-950/30">
          <Avatar user={selected} size={28} />
          <div className="flex flex-col leading-tight min-w-0">
            <span className="font-semibold text-white text-sm truncate max-w-[18rem]" data-testid="post-requested-by-name">
              {selected.name}
            </span>
            <span className={`text-[10px] uppercase tracking-wider ${(TIERS[selected.role] || TIERS.member).color}`}>
              {(TIERS[selected.role] || TIERS.member).label}
            </span>
          </div>
          <div className="ml-auto flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => { onChange(null); setOpen(true); }}
              className="text-zinc-400 hover:text-rose-300"
              data-testid="post-requested-by-change"
            >
              Change
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange(null)}
              className="text-zinc-400 hover:text-rose-300 px-2"
              data-testid="post-requested-by-clear"
              title="Clear"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ) : (
        // Empty state — input + dropdown
        <div className="mt-1">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
            <Input
              value={q}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              placeholder="Search by name or email…"
              className="bg-black/40 border-white/10 pl-8"
              data-testid="post-requested-by-search"
            />
          </div>
          {open && (q.trim() || loading) && (
            <div
              className="mt-1 max-h-72 overflow-auto rounded-md border border-white/10 bg-zinc-950 shadow-xl"
              data-testid="post-requested-by-results"
            >
              {loading && (
                <div className="px-3 py-2 text-xs text-zinc-500">Searching…</div>
              )}
              {!loading && results.length === 0 && (
                <div className="px-3 py-2 text-xs text-zinc-500">No users match “{q}”.</div>
              )}
              {!loading && results.map((u) => {
                const tier = TIERS[u.role] || TIERS.member;
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => pickUser(u)}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-rose-950/40 transition text-left"
                    data-testid={`post-requested-by-result-${u.id}`}
                  >
                    <Avatar user={u} size={28} />
                    <div className="flex flex-col leading-tight min-w-0 flex-1">
                      <span className="text-sm text-white truncate">{u.name || u.email}</span>
                      <span className="text-[10px] text-zinc-500 truncate">{u.email}</span>
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider whitespace-nowrap ${tier.color}`}>
                      {tier.label}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
